/**
 * Drag handles on the Recorded wall's dividers: pull a vertical line to trade width between the
 * two columns beside it, a horizontal one to trade height between two rows. Double-click a line
 * to even its two sides out; arrow keys nudge it when it has focus.
 *
 * An overlay inside the (positioned) grid container — absolutely placed, so it takes no grid
 * cell. A handle is drawn only where the line really is a window edge: the stretch of the
 * column 1|2 line that runs THROUGH the big window is not a divider anyone can see, so there is
 * nothing to grab there.
 *
 * Sizes are CSS `fr` weights. The host owns them; `onChange` fires on every move so the wall
 * resizes live under the pointer. Used by the Recorded wall (sizes in its stored layout) and by
 * both live walls (sizes per saved layout via useWallTrackSizes).
 *
 * The handle's pointer events stop here: the live wall's grid starts a merge selection on
 * pointerdown, and a resize must not also start one.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import type { WallWindow } from '../live-wall/wallLayout';

export interface WallGridResizerProps {
  rows: number;
  cols: number;
  windows: WallWindow[];
  colSizes: number[];
  rowSizes: number[];
  onChange: (next: { colSizes?: number[]; rowSizes?: number[] }) => void;
}

/** Narrowest a column / shortest a row may be dragged — still a usable picture. */
const MIN_COL_PX = 120;
const MIN_ROW_PX = 80;
const HIT_PX = 12;

type Axis = 'col' | 'row';

const evenSizes = (n: number) => Array.from({ length: n }, () => 1);
const fits = (v: unknown, n: number): v is number[] =>
  Array.isArray(v) && v.length === n && v.every((x) => typeof x === 'number' && Number.isFinite(x) && x > 0.05 && x < 20);

/**
 * Column widths / row heights for a grid whose shape the host already owns (the live walls'
 * saved layouts), remembered in this browser under `key` — one entry per layout. A stored
 * entry for a different rows×cols is ignored, so changing a layout's grid size starts even.
 * `key` null (nothing loaded) keeps sizes in memory only.
 */
export function useWallTrackSizes(key: string | null, rows: number, cols: number) {
  const read = useCallback((): { colSizes: number[]; rowSizes: number[] } => {
    const even = { colSizes: evenSizes(cols), rowSizes: evenSizes(rows) };
    if (!key) return even;
    try {
      const v = JSON.parse(window.localStorage.getItem(key) || 'null');
      return v && fits(v.colSizes, cols) && fits(v.rowSizes, rows) ? { colSizes: v.colSizes, rowSizes: v.rowSizes } : even;
    } catch { return even; }
  }, [key, rows, cols]);
  const [state, setState] = useState(() => ({ key, rows, cols, ...read() }));
  // Another layout, or this one's grid size changed → its own sizes (or even ones).
  let cur = state;
  if (state.key !== key || state.rows !== rows || state.cols !== cols) {
    cur = { key, rows, cols, ...read() };
    setState(cur);
  }
  const write = useCallback((next: { colSizes: number[]; rowSizes: number[] }) => {
    if (!key) return;
    try { window.localStorage.setItem(key, JSON.stringify(next)); } catch { /* private mode */ }
  }, [key]);
  const onChange = useCallback((patch: { colSizes?: number[]; rowSizes?: number[] }) => {
    setState((s) => {
      const next = { ...s, ...patch };
      write({ colSizes: next.colSizes, rowSizes: next.rowSizes });
      return next;
    });
  }, [write]);
  const reset = useCallback(() => onChange({ colSizes: evenSizes(cols), rowSizes: evenSizes(rows) }), [onChange, rows, cols]);
  const uneven = cur.colSizes.some((f) => Math.abs(f - 1) > 0.01) || cur.rowSizes.some((f) => Math.abs(f - 1) > 0.01);
  return { colSizes: cur.colSizes, rowSizes: cur.rowSizes, onChange, reset, uneven };
}

/** `minmax(0, Nfr) …` — a grid template for these weights. */
export const trackTemplate = (sizes: number[]) => sizes.map((f) => `minmax(0, ${f}fr)`).join(' ');
interface Handle { axis: Axis; index: number; from: number; to: number }

export default function WallGridResizer({ rows, cols, windows, colSizes, rowSizes, onChange }: WallGridResizerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0, colGap: 0, rowGap: 0 });
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const el = ref.current;
    const grid = el?.parentElement;
    if (!el || !grid) return undefined;
    const measure = () => {
      const cs = window.getComputedStyle(grid);
      const px = (v: string) => parseFloat(v) || 0;
      setBox({
        w: grid.clientWidth - px(cs.paddingLeft) - px(cs.paddingRight),
        h: grid.clientHeight - px(cs.paddingTop) - px(cs.paddingBottom),
        colGap: px(cs.columnGap),
        rowGap: px(cs.rowGap),
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(grid);
    return () => ro.disconnect();
  }, []);

  // Track offsets: where each column / row starts and ends, in px from the grid's content edge.
  const tracks = (sizes: number[], total: number, gap: number) => {
    const avail = Math.max(0, total - gap * (sizes.length - 1));
    const sum = sizes.reduce((a, b) => a + b, 0) || 1;
    const out: { start: number; end: number }[] = [];
    let at = 0;
    for (const f of sizes) {
      const len = (avail * f) / sum;
      out.push({ start: at, end: at + len });
      at += len + gap;
    }
    return { out, pxPerFr: avail / sum };
  };
  const colT = tracks(colSizes, box.w, box.colGap);
  const rowT = tracks(rowSizes, box.h, box.rowGap);

  // Which window covers each cell — a line between two cells is an edge only if they differ.
  const handles = useMemo(() => {
    const owner: number[] = Array(rows * cols).fill(-1);
    for (const w of windows) {
      for (let r = w.row; r < w.row + w.rowSpan; r++) for (let c = w.col; c < w.col + w.colSpan; c++) owner[r * cols + c] = w.position;
    }
    const out: Handle[] = [];
    const runs = (axis: Axis, index: number, n: number, isEdge: (k: number) => boolean) => {
      let from = -1;
      for (let k = 0; k <= n; k++) {
        const edge = k < n && isEdge(k);
        if (edge && from < 0) from = k;
        if (!edge && from >= 0) { out.push({ axis, index, from, to: k - 1 }); from = -1; }
      }
    };
    for (let c = 0; c < cols - 1; c++) runs('col', c, rows, (r) => owner[r * cols + c] !== owner[r * cols + c + 1]);
    for (let r = 0; r < rows - 1; r++) runs('row', r, cols, (c) => owner[r * cols + c] !== owner[(r + 1) * cols + c]);
    return out;
  }, [rows, cols, windows]);

  /** Move line `index` by `deltaPx`, trading size between its two neighbours. */
  const shift = (axis: Axis, index: number, base: number[], deltaPx: number) => {
    const pxPerFr = axis === 'col' ? colT.pxPerFr : rowT.pxPerFr;
    if (!pxPerFr) return;
    const pair = base[index] + base[index + 1];
    const minFr = Math.min(pair / 2, (axis === 'col' ? MIN_COL_PX : MIN_ROW_PX) / pxPerFr);
    const a = Math.min(pair - minFr, Math.max(minFr, base[index] + deltaPx / pxPerFr));
    const next = [...base];
    next[index] = a;
    next[index + 1] = pair - a;
    onChange(axis === 'col' ? { colSizes: next } : { rowSizes: next });
  };

  const startDrag = (h: Handle, key: string) => (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    const origin = h.axis === 'col' ? e.clientX : e.clientY;
    const base = h.axis === 'col' ? [...colSizes] : [...rowSizes];
    setActive(key);
    const prevCursor = document.body.style.cursor;
    document.body.style.cursor = h.axis === 'col' ? 'col-resize' : 'row-resize';
    const move = (ev: globalThis.PointerEvent) => {
      ev.stopPropagation();
      shift(h.axis, h.index, base, (h.axis === 'col' ? ev.clientX : ev.clientY) - origin);
    };
    const end = (ev: globalThis.PointerEvent) => {
      ev.stopPropagation();
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', end);
      el.removeEventListener('pointercancel', end);
      document.body.style.cursor = prevCursor;
      setActive(null);
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
  };

  const even = (h: Handle) => {
    const base = h.axis === 'col' ? [...colSizes] : [...rowSizes];
    const mean = (base[h.index] + base[h.index + 1]) / 2;
    base[h.index] = mean;
    base[h.index + 1] = mean;
    onChange(h.axis === 'col' ? { colSizes: base } : { rowSizes: base });
  };

  const onKey = (h: Handle) => (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 48 : 12;
    const back = h.axis === 'col' ? 'ArrowLeft' : 'ArrowUp';
    const fwd = h.axis === 'col' ? 'ArrowRight' : 'ArrowDown';
    if (e.key !== back && e.key !== fwd) return;
    e.preventDefault();
    e.stopPropagation(); // the page's arrow keys skip playback
    shift(h.axis, h.index, h.axis === 'col' ? colSizes : rowSizes, e.key === fwd ? step : -step);
  };

  if (!box.w || !box.h) return <div ref={ref} className="pointer-events-none absolute inset-0" aria-hidden />;

  return (
    <div ref={ref} className="pointer-events-none absolute inset-0 z-20">
      {handles.map((h) => {
        const key = `${h.axis}-${h.index}-${h.from}`;
        const isCol = h.axis === 'col';
        const t = isCol ? colT.out : rowT.out;
        const across = isCol ? rowT.out : colT.out;
        const line = (t[h.index].end + t[h.index + 1].start) / 2;
        const from = across[h.from].start;
        const to = across[h.to].end;
        const on = active === key;
        const style = isCol
          ? { left: line - HIT_PX / 2, top: from, width: HIT_PX, height: to - from }
          : { top: line - HIT_PX / 2, left: from, height: HIT_PX, width: to - from };
        return (
          <div
            key={key}
            role="separator"
            aria-orientation={isCol ? 'vertical' : 'horizontal'}
            aria-label={isCol ? `Resize columns ${h.index + 1} and ${h.index + 2}` : `Resize rows ${h.index + 1} and ${h.index + 2}`}
            tabIndex={0}
            title="Drag to resize · double-click to even out"
            onPointerDown={startDrag(h, key)}
            onDoubleClick={() => even(h)}
            onKeyDown={onKey(h)}
            style={style}
            className={`group pointer-events-auto absolute flex touch-none items-center justify-center outline-none ${isCol ? 'cursor-col-resize' : 'cursor-row-resize'}`}
          >
            <span
              // Opacity rather than `bg-brand/70`: the theme colours are bare var()s, and Tailwind
              // emits nothing for an opacity modifier on those.
              className={`rounded-full bg-brand transition-opacity ${isCol ? 'h-full w-[3px]' : 'h-[3px] w-full'} ${
                on ? 'opacity-100' : 'opacity-0 group-hover:opacity-70 group-focus-visible:opacity-100'
              }`}
            />
            {/* A grip in the middle so the line can be found before it is hovered. */}
            <span
              className={`absolute rounded-full border border-border bg-card shadow-sm transition-opacity ${isCol ? 'h-8 w-2' : 'h-2 w-8'} ${
                on ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'
              }`}
            />
          </div>
        );
      })}
    </div>
  );
}
